import type { ChatMessage, ChatRoom } from "@talkers/shared";
import { createRoot, createSignal } from "solid-js";
import { createStore } from "solid-js/store";
import { api } from "@/api/client";

export const {
  chatOpen,
  openChat,
  closeChat,
  rooms,
  roomsLoading,
  activeRoomId,
  selectRoom,
  messages,
  messagesLoading,
  openWithUser,
  sendMessage,
  receiveMessage,
  refreshRooms,
} = createRoot(() => {
  const [chatOpen, setChatOpen] = createSignal(false);
  const [rooms, setRooms] = createSignal<ChatRoom[]>([]);
  const [activeRoomId, setActiveRoomId] = createSignal<number | null>(null);
  const [messages, setMessages] = createStore<Record<number, ChatMessage[]>>({});

  /**
   * 取得中かどうか。ダイアログは即座に開けて、中身が来るまでは待っている形を出す。
   * 開くのを待たせると、押しても何も起きていないように見えるため。
   */
  const [roomsLoading, setRoomsLoading] = createSignal(false);
  const [messagesLoading, setMessagesLoading] = createSignal(false);

  const refreshRooms = async () => {
    setRoomsLoading(true);
    try {
      setRooms((await api.listChatRooms()).data);
    } finally {
      setRoomsLoading(false);
    }
  };

  const loadMessages = async (roomId: number) => {
    // 一度読んだ部屋は残っているので、そのときは待ちを出さずに差し替える
    setMessagesLoading(messages[roomId] === undefined);
    try {
      setMessages(roomId, (await api.listChatMessages(roomId)).data);
    } finally {
      setMessagesLoading(false);
    }
  };

  return {
    chatOpen,
    rooms,
    roomsLoading,
    activeRoomId,
    messages,
    messagesLoading,
    refreshRooms,
    closeChat: () => setChatOpen(false),

    async openChat() {
      setChatOpen(true);
      await refreshRooms();
      const first = rooms()[0];
      if (activeRoomId() === null && first !== undefined) {
        setActiveRoomId(first.id);
        await loadMessages(first.id);
      }
    },

    async selectRoom(roomId: number) {
      setActiveRoomId(roomId);
      await loadMessages(roomId);
    },

    async openWithUser(userId: number) {
      // 先に開く。部屋を作る往復のあいだ何も起きないように見せない
      setChatOpen(true);
      setRoomsLoading(true);
      setMessagesLoading(true);
      const room = (await api.openChatRoom(userId)).data;
      setActiveRoomId(room.id);
      await refreshRooms();
      await loadMessages(room.id);
      setMessagesLoading(false);
    },

    async sendMessage(roomId: number, body: string) {
      await api.sendChatMessage(roomId, body);
    },

    receiveMessage(message: ChatMessage) {
      const current = messages[message.roomId];
      if (current !== undefined && !current.some((existing) => existing.id === message.id)) {
        setMessages(message.roomId, [...current, message]);
      }
      void refreshRooms();
    },
  };
});
